// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import { MatDialog, MatPaginator, MatSort } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  startWith, tap
} from "rxjs/operators";

// Core packages
import { ViewPlatformNotesDialogComponent } from "src/app/components/platform-note-card/components/view-platform-notes-dialog/view-platform-notes-dialog.component";
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { CompaniesDataSource } from "src/app/shared/utils/data-sources/companies-data-source";

@Component({
  selector: "app-companies-list",
  templateUrl: "./companies-list.page.html",
  styleUrls: ["./companies-list.page.scss"],
})
export class CompaniesListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "hasNotes",
    "types",
    "name",
    "totalProperties",
    // "email",
    // "legalAddressCity",
    "documentUpload",
    "documentUploadNumber",
    "privacy",
    "privacyNumber",
    "subscriptionSuppliers",
    "subscriptionSuppliersInvoice",
    "subscriptionSuppliersNumber",
    "subscriptionTagnfc",
    "subscriptionTagnfcInvoice",
    "subscriptionTagnfcNumber",
  ];
  dataSource: CompaniesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild("input", { static: true }) input: ElementRef;
  typesFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  totalPropertiesFilter: FormControl = new FormControl("");
  emailFilter: FormControl = new FormControl("");
  legalAddressCityFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  suppliersFilter: FormControl = new FormControl("");
  nfcAccessFilter: FormControl = new FormControl("");
  documentUploadFilter: FormControl = new FormControl("");
  documentUploadNumberFilter: FormControl = new FormControl("");
  privacyFilter: FormControl = new FormControl("");
  privacyNumberFilter: FormControl = new FormControl("");
  subscriptionSuppliersInvoiceFilter: FormControl = new FormControl("");
  subscriptionSuppliersNumberFilter: FormControl = new FormControl("");
  subscriptionTagnfcInvoiceFilter: FormControl = new FormControl("");
  subscriptionTagnfcNumberFilter: FormControl = new FormControl("");
  // END - Table properties

  activeRoute: string;
  loggedUserRoleType = "";
  exportTechnicalFileIsLoading = false;

  /**
   * Class constructor
   */
  constructor(
    private companiesService: CompaniesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    public dialog: MatDialog
  ) {}

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("companies.list.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.authService.loggedUserRequestedRoleType$.subscribe((res) => {
      if (res) {
        this.loggedUserRoleType = res;
      }
    });

    // Init data source
    this.dataSource = new CompaniesDataSource(
      this.companiesService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("companies");
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.typesFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.totalPropertiesFilter.valueChanges.pipe(startWith(null as string)),
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.legalAddressCityFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
        this.suppliersFilter.valueChanges.pipe(startWith(null as string)),
        this.nfcAccessFilter.valueChanges.pipe(startWith(null as string)),
        this.documentUploadFilter.valueChanges.pipe(startWith(null as string)),
        this.documentUploadNumberFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.privacyFilter.valueChanges.pipe(startWith(null as string)),
        this.privacyNumberFilter.valueChanges.pipe(startWith(null as string)),
        this.subscriptionSuppliersInvoiceFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.subscriptionSuppliersNumberFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.subscriptionTagnfcInvoiceFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.subscriptionTagnfcNumberFilter.valueChanges.pipe(
          startWith(null as string)
        ),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        })
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      "companies",
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: "", // this.input.nativeElement.value,
        types: this.typesFilter.value,
        name: this.nameFilter.value,
        totalProperties: this.totalPropertiesFilter.value,
        email: this.emailFilter.value,
        legalAddressCity: this.legalAddressCityFilter.value,
        createdAt: this.createdAtFilter.value,
        subscriptionsSuppliers: this.suppliersFilter.value,
        subscriptionTagnfc: this.nfcAccessFilter.value,
        documentUpload: this.documentUploadFilter.value,
        documentUploadNumber: this.documentUploadNumberFilter.value,
        privacy: this.privacyFilter.value,
        privacyNumber: this.privacyNumberFilter.value,
        subscriptionSuppliersInvoice:
          this.subscriptionSuppliersInvoiceFilter.value,
        subscriptionSuppliersNumber:
          this.subscriptionSuppliersNumberFilter.value,
        subscriptionTagnfcInvoice: this.subscriptionTagnfcInvoiceFilter.value,
        subscriptionTagnfcNumber: this.subscriptionTagnfcNumberFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      types: this.typesFilter.value,
      name: this.nameFilter.value,
      totalProperties: this.totalPropertiesFilter.value,
      email: this.emailFilter.value,
      legalAddressCity: this.legalAddressCityFilter.value,
      createdAt: this.createdAtFilter.value
        ? new Date(this.createdAtFilter.value).toISOString()
        : undefined,
      subscriptionsSuppliers: this.suppliersFilter.value,
      subscriptionTagnfc: this.nfcAccessFilter.value,
      documentUpload: this.documentUploadFilter.value,
      documentUploadNumber: this.documentUploadNumberFilter.value,
      privacy: this.privacyFilter.value,
      privacyNumber: this.privacyNumberFilter.value,
      subscriptionSuppliersInvoice:
        this.subscriptionSuppliersInvoiceFilter.value,
      subscriptionSuppliersNumber: this.subscriptionSuppliersNumberFilter.value,
      subscriptionTagnfcInvoice: this.subscriptionTagnfcInvoiceFilter.value,
      subscriptionTagnfcNumber: this.subscriptionTagnfcNumberFilter.value,
    };
    this.dataSource.loadItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export companies in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */
  onExport(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "companies.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.companiesService.getExport().subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.typesFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.totalPropertiesFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.legalAddressCityFilter.setValue(null);
    this.createdAtFilter.setValue(null);
    this.suppliersFilter.setValue(null);
    this.nfcAccessFilter.setValue(null);
    this.documentUploadFilter.setValue(null);
    this.documentUploadNumberFilter.setValue(null);
    this.privacyFilter.setValue(null);
    this.privacyNumberFilter.setValue(null);
    this.subscriptionSuppliersInvoiceFilter.setValue(null);
    this.subscriptionSuppliersNumberFilter.setValue(null);
    this.subscriptionTagnfcInvoiceFilter.setValue(null);
    this.subscriptionTagnfcNumberFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "createdAt";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.types) {
                this.typesFilter.setValue(setting.search.types);
              }
              if (setting.search && setting.search.name) {
                this.nameFilter.setValue(setting.search.name);
              }
              if (setting.search && setting.search.totalProperties) {
                this.totalPropertiesFilter.setValue(
                  setting.search.totalProperties
                );
              }
              if (setting.search && setting.search.email) {
                this.emailFilter.setValue(setting.search.email);
              }
              if (setting.search && setting.search.legalAddressCity) {
                this.legalAddressCityFilter.setValue(
                  setting.search.legalAddressCity
                );
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
              if (setting.search && setting.search.substriptions.nfcAccess) {
                this.nfcAccessFilter.setValue(
                  setting.search.substriptions.nfcAccess
                );
              }
              if (setting.search && setting.search.documentUpload) {
                this.documentUploadFilter.setValue(setting.search.disclaimer);
              }

              if (setting.search && setting.search.documentUploadNumber) {
                this.documentUploadNumberFilter.setValue(
                  setting.search.disclaimerNumber
                );
              }
              if (setting.search && setting.search.privacy) {
                this.privacyFilter.setValue(setting.search.privacy);
              }
              if (setting.search && setting.search.privacyNumber) {
                this.privacyNumberFilter.setValue(
                  setting.search.privacyNumber
                );
              }
              if (
                setting.search &&
                setting.search.subscriptionSuppliersInvoice
              ) {
                this.subscriptionSuppliersInvoiceFilter.setValue(
                  setting.search.subscriptionSuppliersInvoice
                );
              }
              if (setting.search && setting.search.subscriptionSuppliersNumbe) {
                this.subscriptionSuppliersNumberFilter.setValue(
                  setting.search.subscriptionSuppliersNumbe
                );
              }
              if (setting.search && setting.search.subscriptionTagnfcInvoice) {
                this.subscriptionTagnfcInvoiceFilter.setValue(
                  setting.search.subscriptionTagnfcInvoice
                );
              }
              if (setting.search && setting.search.subscriptionTagnfcNumber) {
                this.subscriptionTagnfcNumberFilter.setValue(
                  setting.search.subscriptionTagnfcNumber
                );
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Techincal file export in EXCEL format
   *
   * @since 1.0.0
   */
  onExportTechnicalFile(companyId?: string, companyName?: string): void {
    this.exportTechnicalFileIsLoading = true;
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      `companies.list.technicalFile`
    )}  ${companyName} ${date}`;
    this.subscriptions.push(
      this.companiesService.getTechnicalFile(companyId).subscribe(
        (res) => {
          this.toastrService.success(res.message);
          this.exportTechnicalFileIsLoading = false;
        },
        (err) => {
          if (err && err.message) {
            this.toastrService.error(err.message);
          } else {
            this.helperService.handleError(err);
          }
          this.exportTechnicalFileIsLoading = false;
        }
      )
    );
  }

  /**
   * Open modal of property notes
   *
   * @since 1.0.0
   */
  onOpenNotes(companyId: string): void {
    this.dialog.open(ViewPlatformNotesDialogComponent, {
      data: {
        companyId,
      },
      width: "90%",
    });
  }
}
